import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  MessageSquare, Trash2, Eye, ArrowLeft, Search,
  Filter, CheckCircle, XCircle, AlertTriangle, Pin,
  ThumbsUp, Clock, User, Flag, MoreVertical, X, Loader2, Feather
} from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import { safeFetch } from "../utils/safeFetch";
import { CATEGORIES, labelForTopic } from "../data/communityCategories";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// 새 카테고리(13개 대카테고리 + 서브)에서 어드민 드롭다운용 옵션 추출
// { value: <id>, label: <한글 라벨> } 형태로 정렬된 배열
const CATEGORY_OPTIONS = (() => {
  const opts = [];
  CATEGORIES.forEach((c) => {
    if (!c.isFeed) return;
    if (c.id === "home") return;  // 홈피드는 카테고리가 아님
    // 대카테고리 자체
    opts.push({ value: c.id, label: c.label, isParent: true });
    // 서브카테고리들
    (c.sub || []).forEach((s) => {
      opts.push({ value: s.id, label: `└ ${s.label}`, parent: c.id });
    });
  });
  return opts;
})();

// id → 한글 라벨 변환 (필터 드롭다운에서 사용)
const labelOf = (id) => labelForTopic(id);

const statusLabels = {
  published: { label: "게시중", color: "bg-green-100 text-green-600", icon: CheckCircle },
  hidden: { label: "숨김", color: "bg-stone-100 text-stone-600", icon: XCircle },
  reported: { label: "신고됨", color: "bg-red-100 text-red-600", icon: AlertTriangle },
};

const categoryColors = {
  "연애 & 인간관계": "bg-rose-100 text-rose-700",
  "여행": "bg-sky-100 text-sky-700",
  "건강": "bg-emerald-100 text-emerald-700",
  "취미": "bg-amber-100 text-amber-700",
  "노후 & 인생2막": "bg-indigo-100 text-indigo-700",
  "자유게시판": "bg-blue-100 text-blue-600",
  "오늘의 하루": "bg-pink-100 text-pink-600",
  "익명 고민상담": "bg-stone-200 text-stone-700",
};

export default function AdminPostsPage() {
  const navigate = useNavigate();
  const { isAuthenticated, user, loading, token } = useAuth();
  const [activeTab, setActiveTab] = useState("posts");
  const [posts, setPosts] = useState([]);
  const [viewingPost, setViewingPost] = useState(null);
  const [viewingComments, setViewingComments] = useState([]);
  const [viewingLoading, setViewingLoading] = useState(false);

  const openPostViewer = async (post) => {
    setViewingPost(post);
    setViewingComments([]);
    setViewingLoading(true);
    try {
      // Get full post (to ensure fresh content) + comments
      const [postRes, cmtRes] = await Promise.all([
        fetch(`${API_URL}/api/posts/${post.id}`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_URL}/api/posts/${post.id}/comments`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      if (postRes.ok) {
        const fresh = await postRes.json();
        setViewingPost((prev) => ({ ...prev, ...fresh, content: fresh.content || prev.content }));
      }
      if (cmtRes.ok) {
        const cdata = await cmtRes.json();
        setViewingComments(Array.isArray(cdata) ? cdata : cdata.comments || []);
      }
    } catch {} finally { setViewingLoading(false); }
  };
  const [comments, setComments] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterCategory, setFilterCategory] = useState("all");
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (loading) return;
    if (!isAuthenticated || user?.role !== "admin") {
      navigate("/");
    }
  }, [isAuthenticated, user, loading, navigate]);

  useEffect(() => {
    if (token && user?.role === "admin") {
      fetchPosts();
      fetchComments();
    }
  }, [token]);

  const fetchPosts = async () => {
    try {
      setIsLoading(true);
      const res = await safeFetch(`${API_URL}/api/posts?limit=200`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        const mapped = (data.posts || data || []).map(p => ({
          id: p.id,
          title: p.title,
          content: p.content || "",
          author: p.author,
          author_id: p.author_id,
          category: p.category || "free",
          categoryLabel: labelOf(p.category) || "자유게시판",
          date: p.created_at ? new Date(p.created_at).toISOString().split("T")[0] : "-",
          created_at: p.created_at,
          views: p.views || 0,
          likes: p.likes || 0,
          comments: p.comment_count || 0,
          status: p.status || "published",
          isPinned: p.is_pinned || false,
          isEssay: p.is_essay || false,
          reports: p.reports || 0,
        }));
        setPosts(mapped);
      }
    } catch (err) { console.error("Fetch posts error:", err); }
    finally { setIsLoading(false); }
  };

  const fetchComments = async () => {
    try {
      const res = await safeFetch(`${API_URL}/api/admin/comments?limit=200`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setComments((data.comments || data || []).map(c => ({
          id: c.id,
          postId: c.post_id,
          author: c.author,
          content: c.content,
          date: c.created_at ? new Date(c.created_at).toISOString().split("T")[0] : "-",
          status: c.status || "published",
          reports: c.reports || 0,
        })));
      }
    } catch { /* comments API might not exist yet */ }
  };

  const filteredPosts = posts.filter(post => {
    const matchesSearch = post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         post.author.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = filterStatus === "all" || post.status === filterStatus;
    const matchesCategory = filterCategory === "all" || post.category === filterCategory;
    return matchesSearch && matchesStatus && matchesCategory;
  });

  const filteredComments = comments.filter(comment => {
    const matchesSearch = comment.content.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         comment.author.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = filterStatus === "all" || comment.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const handleDeletePost = async (id) => {
    if (!confirm("정말 이 게시글을 삭제하시겠습니까?")) return;
    try {
      const res = await safeFetch(`${API_URL}/api/posts/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) fetchPosts();
      else alert("삭제에 실패했습니다.");
    } catch { alert("오류가 발생했습니다."); }
  };

  // 게시글 카테고리 인라인 수정 (관리자) — 옵션 value 가 카테고리 ID
  const handleUpdatePostCategory = async (postId, newValue) => {
    // newValue 는 이미 카테고리/서브카테고리 ID (sseom, relationships, free 등)
    try {
      const res = await safeFetch(`${API_URL}/api/posts/${postId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ category: newValue }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "카테고리 변경 실패");
      }
      // 즉시 로컬 반영 + 백엔드에서 재로딩
      setPosts((prev) => prev.map((p) => (p.id === postId ? { ...p, category: newValue, categoryLabel: labelOf(newValue) } : p)));
      fetchPosts();
    } catch (e) {
      console.error("category update failed", e);
      alert(e.message || "카테고리 변경에 실패했습니다.");
    }
  };

  const handleTogglePostStatus = async (id) => {
    const post = posts.find(p => p.id === id);
    const newStatus = post?.status === "published" ? "hidden" : "published";
    try {
      const res = await safeFetch(`${API_URL}/api/posts/${id}/status`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status: newStatus }),
      });
      if (res.ok) fetchPosts();
    } catch { /* fallback to local update */ 
      setPosts(posts.map(p => p.id === id ? { ...p, status: newStatus } : p));
    }
  };

  const handleTogglePin = async (id) => {
    const post = posts.find(p => p.id === id);
    try {
      const res = await safeFetch(`${API_URL}/api/posts/${id}/pin`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ is_pinned: !post?.isPinned }),
      });
      if (res.ok) fetchPosts();
    } catch {
      setPosts(posts.map(p => p.id === id ? { ...p, isPinned: !p.isPinned } : p));
    }
  };

  // 'L양의 연애 에세이' 지정/해제 (관리자) — 기존 글의 is_essay 만 토글, 내용 변경 없음
  const handleToggleEssay = async (id) => {
    const post = posts.find(p => p.id === id);
    try {
      const res = await safeFetch(`${API_URL}/api/admin/posts/${id}/toggle-essay`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ is_essay: !post?.isEssay }),
      });
      if (res.ok) fetchPosts();
      else alert("에세이 지정 변경에 실패했습니다.");
    } catch { alert("오류가 발생했습니다."); }
  };

  const handleDeleteComment = async (id) => {
    if (!confirm("정말 이 댓글을 삭제하시겠습니까?")) return;
    try {
      const res = await safeFetch(`${API_URL}/api/comments/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) fetchComments();
    } catch {
      setComments(comments.filter(c => c.id !== id));
    }
  };

  // Filter handler logic only — categories list no longer needed for dropdown (uses CATEGORY_OPTIONS)

  return (
    <div className="min-h-screen bg-stone-100 pt-20">
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate("/admin")}
            className="flex items-center gap-2 text-stone-500 hover:text-burgundy-500 mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            대시보드로 돌아가기
          </button>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-orange-500 rounded-xl flex items-center justify-center">
              <MessageSquare className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-stone-800">게시판 관리</h1>
              <p className="text-stone-500 text-sm">커뮤니티 게시글 및 댓글 관리</p>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl p-4 border border-stone-200">
            <p className="text-stone-500 text-sm">전체 게시글</p>
            <p className="text-2xl font-bold text-stone-800">{posts.length}</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-stone-200">
            <p className="text-stone-500 text-sm">신고된 게시글</p>
            <p className="text-2xl font-bold text-red-600">
              {posts.filter(p => p.status === "reported" || p.reports > 0).length}
            </p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-stone-200">
            <p className="text-stone-500 text-sm">전체 댓글</p>
            <p className="text-2xl font-bold text-stone-800">{comments.length}</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-stone-200">
            <p className="text-stone-500 text-sm">고정 공지</p>
            <p className="text-2xl font-bold text-burgundy-600">
              {posts.filter(p => p.isPinned).length}
            </p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab("posts")}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === "posts"
                ? "bg-orange-500 text-white"
                : "bg-white text-stone-600 hover:bg-stone-50"
            }`}
          >
            게시글 관리
          </button>
          <button
            onClick={() => setActiveTab("comments")}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === "comments"
                ? "bg-orange-500 text-white"
                : "bg-white text-stone-600 hover:bg-stone-50"
            }`}
          >
            댓글 관리
          </button>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl p-4 mb-6 border border-stone-200">
          <div className="flex flex-wrap items-center gap-4">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
              <input
                type="text"
                placeholder="검색..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/20"
              />
            </div>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-2 border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/20"
            >
              <option value="all">전체 상태</option>
              <option value="published">게시중</option>
              <option value="hidden">숨김</option>
              <option value="reported">신고됨</option>
            </select>
            {activeTab === "posts" && (
              <select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                className="px-4 py-2 border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/20"
                data-testid="admin-posts-category-filter"
              >
                <option value="all">전체 카테고리</option>
                {CATEGORY_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}
                    style={opt.isParent ? { fontWeight: 700 } : {}}>
                    {opt.label}
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>

        {/* Posts Table */}
        {activeTab === "posts" && (
          <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-stone-50 border-b border-stone-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-stone-500 uppercase">게시글</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-stone-500 uppercase">카테고리</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-stone-500 uppercase">작성자</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-stone-500 uppercase">작성일</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-stone-500 uppercase">통계</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-stone-500 uppercase">상태</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-stone-500 uppercase">관리</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {filteredPosts.map((post) => {
                    const StatusIcon = statusLabels[post.status].icon;
                    return (
                      <tr key={post.id} className="hover:bg-stone-50">
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-2">
                            {post.isPinned && (
                              <Pin className="w-4 h-4 text-burgundy-500 flex-shrink-0" />
                            )}
                            {post.isEssay && (
                              <span className="shrink-0 inline-flex items-center gap-0.5 text-[10px] font-bold text-rose-600 bg-rose-50 px-1.5 py-0.5 rounded" title="L양의 연애 에세이">
                                <Feather className="w-3 h-3" /> 에세이
                              </span>
                            )}
                            <div>
                              <button
                                onClick={() => openPostViewer(post)}
                                className="font-medium text-stone-800 hover:text-burgundy-500 transition-colors truncate max-w-[250px] text-left block cursor-pointer underline-offset-2 hover:underline"
                                data-testid={`post-title-${post.id}`}
                                title="클릭하여 전체 내용 보기"
                              >
                                {post.title}
                              </button>
                              {post.reports > 0 && (
                                <p className="text-xs text-red-500 flex items-center gap-1">
                                  <Flag className="w-3 h-3" />
                                  신고 {post.reports}건
                                </p>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <select
                            value={post.category || ""}
                            onChange={(e) => handleUpdatePostCategory(post.id, e.target.value)}
                            className={`px-2 py-1 text-xs font-medium rounded-full border border-stone-200 cursor-pointer focus:outline-none focus:ring-2 focus:ring-burgundy-300 ${categoryColors[post.categoryLabel] || "bg-stone-100 text-stone-600"}`}
                            title="카테고리 변경"
                            data-testid="post-category-select"
                          >
                            {CATEGORY_OPTIONS.map((opt) => (
                              <option key={opt.value} value={opt.value}
                                style={opt.isParent ? { fontWeight: 700 } : {}}>
                                {opt.label}
                              </option>
                            ))}
                            {/* 매핑에 없는 기존 카테고리도 옵션으로 유지 (호환성) */}
                            {!CATEGORY_OPTIONS.find((o) => o.value === post.category) && post.category && (
                              <option value={post.category}>{post.categoryLabel || post.category}</option>
                            )}
                          </select>
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4 text-stone-400" />
                            <span className="text-sm text-stone-600">{post.author}</span>
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <span className="text-sm text-stone-500">{post.date}</span>
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-3 text-xs text-stone-500">
                            <span className="flex items-center gap-1">
                              <Eye className="w-3 h-3" />
                              {post.views}
                            </span>
                            <span className="flex items-center gap-1">
                              <ThumbsUp className="w-3 h-3" />
                              {post.likes}
                            </span>
                            <span className="flex items-center gap-1">
                              <MessageSquare className="w-3 h-3" />
                              {post.comments}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full flex items-center gap-1 w-fit ${statusLabels[post.status].color}`}>
                            <StatusIcon className="w-3 h-3" />
                            {statusLabels[post.status].label}
                          </span>
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              onClick={() => handleToggleEssay(post.id)}
                              className={`p-1.5 rounded-lg transition-colors ${
                                post.isEssay
                                  ? "text-rose-600 bg-rose-50"
                                  : "text-stone-400 hover:text-rose-600 hover:bg-rose-50"
                              }`}
                              title={post.isEssay ? "L양의 에세이 해제" : "L양의 연애 에세이로 지정"}
                              data-testid={`essay-toggle-${post.id}`}
                            >
                              <Feather className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleTogglePin(post.id)}
                              className={`p-1.5 rounded-lg transition-colors ${
                                post.isPinned 
                                  ? "text-burgundy-500 bg-burgundy-50" 
                                  : "text-stone-400 hover:text-burgundy-500 hover:bg-burgundy-50"
                              }`}
                              title={post.isPinned ? "고정 해제" : "상단 고정"}
                            >
                              <Pin className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleTogglePostStatus(post.id)}
                              className={`p-1.5 rounded-lg transition-colors ${
                                post.status === "published"
                                  ? "text-stone-400 hover:text-orange-500 hover:bg-orange-50"
                                  : "text-green-500 bg-green-50"
                              }`}
                              title={post.status === "published" ? "숨기기" : "게시하기"}
                            >
                              {post.status === "published" ? (
                                <XCircle className="w-4 h-4" />
                              ) : (
                                <CheckCircle className="w-4 h-4" />
                              )}
                            </button>
                            <button
                              onClick={() => handleDeletePost(post.id)}
                              className="p-1.5 text-stone-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                              title="삭제"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {isLoading ? (
              <div className="p-12 text-center">
                <p className="text-stone-500">로딩중...</p>
              </div>
            ) : filteredPosts.length === 0 ? (
              <div className="p-12 text-center">
                <MessageSquare className="w-12 h-12 text-stone-300 mx-auto mb-4" />
                <p className="text-stone-500">게시글이 없습니다</p>
              </div>
            ) : null}
          </div>
        )}

        {/* Comments Table */}
        {activeTab === "comments" && (
          <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-stone-50 border-b border-stone-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-stone-500 uppercase">댓글 내용</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-stone-500 uppercase">작성자</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-stone-500 uppercase">작성일</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-stone-500 uppercase">상태</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-stone-500 uppercase">관리</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {filteredComments.map((comment) => {
                    const StatusIcon = statusLabels[comment.status].icon;
                    return (
                      <tr key={comment.id} className="hover:bg-stone-50">
                        <td className="px-4 py-4">
                          <div>
                            <p className="text-stone-800 truncate max-w-[300px]">{comment.content}</p>
                            {comment.reports > 0 && (
                              <p className="text-xs text-red-500 flex items-center gap-1 mt-1">
                                <Flag className="w-3 h-3" />
                                신고 {comment.reports}건
                              </p>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4 text-stone-400" />
                            <span className="text-sm text-stone-600">{comment.author}</span>
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <span className="text-sm text-stone-500">{comment.date}</span>
                        </td>
                        <td className="px-4 py-4">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full flex items-center gap-1 w-fit ${statusLabels[comment.status].color}`}>
                            <StatusIcon className="w-3 h-3" />
                            {statusLabels[comment.status].label}
                          </span>
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              onClick={() => handleDeleteComment(comment.id)}
                              className="p-1.5 text-stone-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                              title="삭제"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {filteredComments.length === 0 && (
              <div className="p-12 text-center">
                <MessageSquare className="w-12 h-12 text-stone-300 mx-auto mb-4" />
                <p className="text-stone-500">댓글이 없습니다</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* 📖 Post Viewer Modal */}
      {viewingPost && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-stone-900/70 backdrop-blur-sm p-4 sm:p-6"
          onClick={() => setViewingPost(null)}
          data-testid="post-viewer-modal"
        >
          <div
            className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden shadow-2xl flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="px-6 py-4 border-b border-stone-100 flex items-start justify-between gap-3 bg-gradient-to-r from-stone-50 to-white">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                  <span className={`px-2 py-0.5 text-[10px] font-semibold rounded-full ${categoryColors[viewingPost.categoryLabel] || "bg-stone-100 text-stone-600"}`}>
                    {viewingPost.categoryLabel || labelOf(viewingPost.category)}
                  </span>
                  {viewingPost.isPinned && (
                    <span className="text-[10px] text-burgundy-600 bg-burgundy-50 px-2 py-0.5 rounded-full flex items-center gap-1">
                      <Pin className="w-2.5 h-2.5" /> 고정
                    </span>
                  )}
                  <span className="text-[10px] text-stone-400">{viewingPost.date || "-"}</span>
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-stone-800 leading-snug" data-testid="post-viewer-title">
                  {viewingPost.title}
                </h3>
                <div className="flex items-center gap-3 mt-2 text-xs text-stone-500">
                  <span className="flex items-center gap-1"><User className="w-3 h-3" />{viewingPost.author || "익명"}</span>
                  <span className="flex items-center gap-1"><Eye className="w-3 h-3" />{viewingPost.views || 0}</span>
                  <span className="flex items-center gap-1"><ThumbsUp className="w-3 h-3" />{viewingPost.likes || 0}</span>
                  <span className="flex items-center gap-1"><MessageSquare className="w-3 h-3" />{viewingComments.length}</span>
                </div>
              </div>
              <button
                onClick={() => setViewingPost(null)}
                className="p-2 hover:bg-stone-100 rounded-lg transition-colors flex-shrink-0"
                aria-label="닫기"
                data-testid="close-post-viewer"
              >
                <X className="w-5 h-5 text-stone-500" />
              </button>
            </div>

            {/* Body (scrollable) */}
            <div className="flex-1 overflow-y-auto px-6 py-5">
              {viewingLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 text-burgundy-500 animate-spin" />
                </div>
              ) : (
                <>
                  <div className="prose prose-stone prose-sm max-w-none">
                    <p className="text-stone-700 whitespace-pre-wrap leading-[1.85] text-[15px]" data-testid="post-viewer-content">
                      {viewingPost.content || <span className="text-stone-400 italic">내용이 없습니다.</span>}
                    </p>
                  </div>

                  {viewingPost.image_urls && viewingPost.image_urls.length > 0 && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-5">
                      {viewingPost.image_urls.map((url, i) => (
                        <img
                          key={i}
                          src={url.startsWith("http") ? url : `${API_URL}${url}`}
                          alt=""
                          className="rounded-lg object-cover w-full h-32"
                        />
                      ))}
                    </div>
                  )}

                  {/* Comments */}
                  <div className="mt-6 pt-5 border-t border-stone-100">
                    <h4 className="text-sm font-semibold text-stone-700 mb-3 flex items-center gap-2">
                      <MessageSquare className="w-4 h-4 text-stone-400" />
                      댓글 {viewingComments.length}개
                    </h4>
                    {viewingComments.length === 0 ? (
                      <p className="text-sm text-stone-400 text-center py-4">댓글이 없습니다</p>
                    ) : (
                      <div className="space-y-3">
                        {viewingComments.map((c) => (
                          <div key={c.id} className="bg-stone-50 rounded-lg p-3 border border-stone-100">
                            <div className="flex items-center gap-2 mb-1 text-xs">
                              <span className="font-semibold text-stone-700">{c.author || "익명"}</span>
                              <span className="text-stone-400">
                                {c.created_at ? new Date(c.created_at).toLocaleDateString("ko-KR") : ""}
                              </span>
                            </div>
                            <p className="text-sm text-stone-700 whitespace-pre-wrap break-words">{c.content}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>

            {/* Footer Actions */}
            <div className="border-t border-stone-100 px-6 py-3 bg-stone-50/50 flex items-center justify-between flex-wrap gap-2">
              <span className="text-[11px] text-stone-400">ID: {viewingPost.id?.slice(0, 8)}...</span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => { handleToggleEssay(viewingPost.id); setViewingPost((p) => ({ ...p, isEssay: !p.isEssay })); }}
                  className={`text-xs px-3 py-1.5 rounded-lg font-medium transition-colors ${viewingPost.isEssay ? "bg-rose-100 text-rose-700 hover:bg-rose-200" : "bg-stone-100 text-stone-600 hover:bg-stone-200"}`}
                  data-testid="viewer-essay-toggle"
                >
                  <Feather className="w-3 h-3 inline mr-1" />
                  {viewingPost.isEssay ? "에세이 해제" : "L양의 에세이 지정"}
                </button>
                <button
                  onClick={() => { handleTogglePin(viewingPost.id); setViewingPost((p) => ({ ...p, isPinned: !p.isPinned })); }}
                  className={`text-xs px-3 py-1.5 rounded-lg font-medium transition-colors ${viewingPost.isPinned ? "bg-burgundy-100 text-burgundy-700 hover:bg-burgundy-200" : "bg-stone-100 text-stone-600 hover:bg-stone-200"}`}
                  data-testid="viewer-pin-toggle"
                >
                  <Pin className="w-3 h-3 inline mr-1" />
                  {viewingPost.isPinned ? "고정 해제" : "상단 고정"}
                </button>
                <button
                  onClick={() => setViewingPost(null)}
                  className="text-xs px-4 py-1.5 rounded-lg bg-white border border-stone-200 text-stone-600 hover:bg-stone-100 transition-colors"
                >
                  닫기
                </button>
                <button
                  onClick={() => { if (window.confirm("정말 삭제하시겠습니까?")) { handleDeletePost(viewingPost.id); setViewingPost(null); } }}
                  className="text-xs px-3 py-1.5 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 transition-colors flex items-center gap-1"
                  data-testid="viewer-delete-btn"
                >
                  <Trash2 className="w-3 h-3" /> 삭제
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
